import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Notice,
  ProgressBar,
  Badge,
  AnalyticalTable,
  type AnalyticalColumn,
} from "@conuti-das/prince-ui";
import { Flex, Stack, Spinner } from "@/ui-prince";
import {
  mapperApi,
  type MapperBundle,
  type MappingEntry,
  type Bo4eTarget,
  type EdiAnchor,
  type Coverage,
  type BoView,
  type ReviewResult,
  type ReviewFinding,
} from "@/api/mapperApi";
import { EntryEditWizard } from "./EntryEditWizard";
import { ReviewPanel } from "./ReviewPanel";
import { applyMappingFilters, buildMappingRows, type RowFilter } from "./mappingTable";
import { MappingTopBar } from "./MappingTopBar";
import { MappingBridge } from "./MappingBridge";
import { TestPanel } from "./TestPanel";
import { buildEdiBridge, buildBoBridge, sidesFor } from "./bridgeModel";
import type { BridgeRow, Perspective } from "./bridgeModel";

/** Offener Wizard: edit (bestehendes Entry) oder create (Anker aus der Struktur
 * oder BO4E-Ziel aus der BO-Perspektive). */
type WizardState =
  | { mode: "edit"; entry: MappingEntry; msgType: string }
  | { mode: "create"; anchor: EdiAnchor }
  | { mode: "create"; bo4eTarget: { bo: string; path: string } };

/** Maps entry status to a badge tone */
function statusTone(status: string): "green" | "orange" | "neutral" {
  if (status === "approved") return "green";
  if (status === "needs_review") return "orange";
  return "neutral";
}

/** Maps transform type to a badge tone */
function transformTone(transform: string): "orange" | "teal" {
  return transform === "manual" ? "orange" : "teal";
}

export function MappingPage() {
  const [bundles, setBundles] = useState<MapperBundle[]>([]);
  const [bundle, setBundle] = useState<MapperBundle | null>(null);
  const [msgTypes, setMsgTypes] = useState<string[]>([]);
  const [msgType, setMsgType] = useState("");
  const [entries, setEntries] = useState<MappingEntry[]>([]);
  const [bo4eTargets, setBo4eTargets] = useState<Bo4eTarget[]>([]);
  const [ediAnchors, setEdiAnchors] = useState<EdiAnchor[]>([]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [rowFilter, setRowFilter] = useState<RowFilter>("all");
  const [searchText, setSearchText] = useState("");
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Brücke-State
  const [perspective, setPerspective] = useState<Perspective>("msgtype");
  const [businessObjects, setBusinessObjects] = useState<string[]>([]);
  const [boSelection, setBoSelection] = useState("");
  const [msgTypeFilter, setMsgTypeFilter] = useState<string | null>(null);
  const [boView, setBoView] = useState<BoView | null>(null);
  const [swapped, setSwapped] = useState<boolean>(() => localStorage.getItem("mapping.swapped") === "1");
  const [tableMode, setTableMode] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  // KI-Review (Teilprojekt C)
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewResult, setReviewResult] = useState<ReviewResult | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // Scope/Target des laufenden Reviews — bestimmt, in welchen Nachrichtentyp
  // übernommene Drafts geschrieben werden.
  const [reviewScope, setReviewScope] = useState<"message_type" | "bo">("message_type");
  const [reviewTarget, setReviewTarget] = useState("");

  // Bundle + Nachrichtentypen laden
  useEffect(() => {
    mapperApi
      .getBundles()
      .then((bs) => {
        if (bs.length === 0) {
          setError("Keine Mapping-Bundles — erst /erzeuge-bo4e-edifact-mapping ausführen.");
          return;
        }
        setBundles(bs);
        setBundle(bs[0]);
        return mapperApi.getMessageTypes(bs[0].id).then((mt) => {
          setMsgTypes(mt.message_types);
          if (mt.message_types.includes("UTILMDS")) setMsgType("UTILMDS");
          else if (mt.message_types[0]) setMsgType(mt.message_types[0]);
        });
      })
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    localStorage.setItem("mapping.swapped", swapped ? "1" : "0");
  }, [swapped]);

  // Geschäftsobjekte je Bundle laden (BO-Perspektive)
  useEffect(() => {
    if (!bundle) return;
    mapperApi
      .getBusinessObjects(bundle.id)
      .then((r) => {
        const bos = r.business_objects ?? [];
        setBusinessObjects(bos);
        setBoSelection((prev) => prev || bos[0] || "");
      })
      .catch(() => setBusinessObjects([]));
  }, [bundle]);

  // Beim Wechsel in die BO-Perspektive den aktuellen Nachrichtentyp als Filter vorbelegen
  useEffect(() => {
    if (perspective === "bo") setMsgTypeFilter(msgType || null);
  }, [perspective]); // eslint-disable-line react-hooks/exhaustive-deps

  // BO-View laden (BO-Perspektive)
  useEffect(() => {
    if (!bundle || perspective !== "bo" || !boSelection) return;
    mapperApi
      .getBoView(bundle.id, boSelection, msgTypeFilter ?? undefined)
      .then(setBoView)
      .catch(() => setBoView(null));
  }, [bundle, perspective, boSelection, msgTypeFilter]);

  // Entries + Coverage pro Nachrichtentyp (refetch nach Edit via reloadKey)
  const [reloadKey, setReloadKey] = useState(0);
  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  // ---- KI-Review (Teilprojekt C) ----
  const runReview = useCallback(
    async (mode: "check" | "fill_gaps") => {
      if (!bundle) return;
      const scope: "message_type" | "bo" = perspective === "bo" ? "bo" : "message_type";
      const target = perspective === "bo" ? boSelection : msgType;
      setReviewScope(scope);
      setReviewTarget(target);
      setReviewOpen(true);
      setReviewLoading(true);
      setReviewError(null);
      setReviewResult(null);
      try {
        const res = await mapperApi.reviewMapping({ scope, target, bundle: bundle.id, mode });
        setReviewResult(res);
      } catch (e) {
        setReviewError(String(e));
      } finally {
        setReviewLoading(false);
      }
    },
    [bundle, perspective, boSelection, msgType],
  );

  // Nachrichtentyp, in den ein übernommener Vorschlag geschrieben wird.
  const acceptMsgType = useCallback(
    (finding: ReviewFinding): string => {
      if (reviewScope === "message_type") return reviewTarget;
      return (finding as { message_type?: string }).message_type || msgTypeFilter || msgType;
    },
    [reviewScope, reviewTarget, msgTypeFilter, msgType],
  );

  const acceptFinding = useCallback(
    async (finding: ReviewFinding) => {
      if (!bundle || !finding.bo4e_anchor?.bo || !finding.bo4e_anchor?.path) return;
      await mapperApi.createEntry(
        acceptMsgType(finding),
        {
          edi_anchor: (finding.edi_anchor ?? {}) as Record<string, unknown>,
          bo4e_anchor: finding.bo4e_anchor,
          transform: finding.suggested_transform ?? { type: "manual" },
          direction: finding.suggested_direction ?? "both",
          notes: "KI-Vorschlag (Review)",
          status: "draft",
        },
        bundle.id,
      );
      refetch();
    },
    [bundle, acceptMsgType, refetch],
  );

  const acceptAllFindings = useCallback(async () => {
    const missing = (reviewResult?.findings ?? []).filter((f) => f.kind === "missing");
    for (const f of missing) {
      try {
        await acceptFinding(f);
      } catch {
        // einzelne Fehler überspringen — Rest läuft weiter
      }
    }
    setReviewOpen(false);
  }, [reviewResult, acceptFinding]);

  // Edit-Wizard für ein bestehendes Mapping öffnen.
  // Msgtype-Perspektive: das Entry liegt direkt in `entries`. BO-Perspektive:
  // die Match-IDs stammen aus der BO-View (ggf. anderer Nachrichtentyp als der
  // gerade geladene), also den Nachrichtentyp der Quelle bestimmen und das
  // vollständige Entry (inkl. version/direction/notes) dafür nachladen.
  const openEntryById = useCallback(
    async (mappingId: string) => {
      const local = entries.find((x) => x.mapping_id === mappingId);
      if (local) {
        setWizard({ mode: "edit", entry: local, msgType });
        return;
      }
      if (!bundle) return;
      // Quelle in der BO-View finden → ihren Nachrichtentyp ableiten.
      let sourceMsgType: string | undefined;
      for (const f of boView?.fields ?? []) {
        const s = f.sources.find((src) => src.mapping_id === mappingId);
        if (s) {
          sourceMsgType = s.message_type;
          break;
        }
      }
      const mt = sourceMsgType ?? msgType;
      try {
        const m = await mapperApi.getMappings(bundle.id, mt);
        const e = m.entries.find((x) => x.mapping_id === mappingId);
        if (e) setWizard({ mode: "edit", entry: e, msgType: mt });
      } catch {
        // stiller Fallback — Klick tut dann nichts, statt zu crashen
      }
    },
    [entries, bundle, boView, msgType],
  );

  useEffect(() => {
    if (!bundle || !msgType) return;
    setBusy(true);
    Promise.all([
      mapperApi.getMappings(bundle.id, msgType),
      mapperApi.getCoverage(bundle.id, msgType),
    ])
      .then(([m, c]) => {
        setEntries(m.entries);
        setCoverage(c);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setBusy(false));
  }, [bundle, msgType, reloadKey]);

  // Msgtype-Wechsel: Filter zurücksetzen (nicht bei Edit-Refetch)
  useEffect(() => {
    setRowFilter("all");
    setSearchText("");
  }, [msgType]);

  // Skelett (EDI-Anker + BO4E-Targets) für den Edit-Wizard — lazy, einmal pro Msgtype
  useEffect(() => {
    if (!bundle || !msgType) return;
    mapperApi
      .getSkeleton(bundle.id, msgType)
      .then((sk) => {
        setBo4eTargets(sk.bo4e_targets ?? []);
        setEdiAnchors(sk.edi_anchors ?? []);
      })
      .catch(() => {
        // Skelett ist optional — Tabelle bleibt nutzbar, Wizard zeigt einen Hinweis
        setBo4eTargets([]);
        setEdiAnchors([]);
      });
  }, [bundle, msgType]);

  // nr → seg_name aus dem Skelett (für die zweizeilige EDI-Anker-Spalte)
  const segNameByNr = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of ediAnchors) {
      if (a.nr && a.seg_name && !m.has(a.nr)) m.set(a.nr, a.seg_name);
    }
    return m;
  }, [ediAnchors]);

  const tableData = useMemo(
    () => applyMappingFilters(buildMappingRows(entries), { rowFilter, searchText }),
    [entries, rowFilter, searchText],
  );

  type MappingRow = ReturnType<typeof buildMappingRows>[number];

  const columns = useMemo<AnalyticalColumn<MappingRow>[]>(
    () => [
      {
        header: "EDI-Anker",
        accessorKey: "anchor",
        width: "280px",
        cellRender: (row) => {
          const nr = (row as { nr?: string } | undefined)?.nr;
          const segName = nr ? segNameByNr.get(nr) : undefined;
          return segName ? (
            <span style={{ lineHeight: 1.2 }}>
              <span style={{ display: "block" }}>{segName}</span>
              <span
                style={{
                  display: "block",
                  fontFamily: "var(--sapFontMonoFamily, monospace)",
                  fontSize: "0.75rem",
                  color: "var(--sapContent_LabelColor)",
                }}
              >
                {(row as { anchor?: string }).anchor}
              </span>
            </span>
          ) : (
            <span style={{ fontFamily: "var(--sapFontMonoFamily, monospace)" }}>
              {(row as { anchor?: string }).anchor}
            </span>
          );
        },
      },
      { header: "nr", accessorKey: "nr", width: "80px" },
      {
        header: "BO4E Objekt.Pfad",
        accessorKey: "bo4e",
        cellRender: (row) => (
          <span style={{ fontFamily: "var(--sapFontMonoFamily, monospace)" }}>
            {(row as { bo4e?: string }).bo4e}
          </span>
        ),
      },
      {
        header: "Transform",
        accessorKey: "transform",
        width: "130px",
        cellRender: (row) => {
          const v = (row as { transform?: string }).transform ?? "";
          return <Badge tone={transformTone(v)}>{v}</Badge>;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        width: "130px",
        cellRender: (row) => {
          const v = (row as { status?: string }).status ?? "";
          return <Badge tone={statusTone(v)}>{v}</Badge>;
        },
      },
    ],
    [segNameByNr],
  );

  // Brücke-Zeilen je Perspektive
  const rows: BridgeRow[] = useMemo(() => {
    if (perspective === "bo") return boView ? buildBoBridge(boView) : [];
    const skeleton = {
      format_version: bundle?.format_version ?? "",
      message_type: msgType,
      edi_anchors: ediAnchors,
      bo4e_targets: bo4eTargets,
    };
    return buildEdiBridge(skeleton, entries, {}); // noBo4e set populated later by workstream #2
  }, [perspective, boView, bundle, msgType, ediAnchors, bo4eTargets, entries]);

  const sides = sidesFor(perspective, swapped);

  return (
    <div className="page">
      <div className="page-header">
        <h2 className="page-title">EDI ↔ BO4E Mapping</h2>
        <div className="page-subtitle">
          Mapping-Pflege pro Formatversion. Skelett (mapbare EDI-Anker + BO4E-Felder) +
          kuratierte Entries. Zeile anklicken zum Bearbeiten.
        </div>
      </div>

      {error && (
        <Notice tone="negative">
          {error}
        </Notice>
      )}

      {bundle && (
        <Card>
          <MappingTopBar
            bundles={bundles}
            bundleId={bundle?.id ?? ""}
            onBundle={(id) => setBundle(bundles.find((b) => b.id === id) ?? null)}
            perspective={perspective}
            onPerspective={setPerspective}
            options={perspective === "bo" ? businessObjects : msgTypes}
            selection={perspective === "bo" ? boSelection : msgType}
            onSelection={(v) => (perspective === "bo" ? setBoSelection(v) : setMsgType(v))}
            msgTypeFilter={msgTypeFilter}
            onClearMsgTypeFilter={() => setMsgTypeFilter(null)}
            msgTypes={msgTypes}
            onSetMsgTypeFilter={(mt) => setMsgTypeFilter(mt || null)}
            swapped={swapped}
            onSwap={() => setSwapped((s) => !s)}
            onOpenTest={() => setTestOpen(true)}
            tableMode={tableMode}
            onToggleTable={() => setTableMode((t) => !t)}
            onReview={runReview}
            reviewing={reviewLoading}
          />
        </Card>
      )}

      {coverage && (
        <Card
          title={`Coverage — ${coverage.message_type}`}
          header={
            <span style={{ fontSize: "0.85rem", color: "var(--sapContent_LabelColor)" }}>
              {`${coverage.totals.entries} Entries · ${coverage.totals.unmapped_edi} EDI-Anker unmapped · ${coverage.totals.unmapped_bo4e} BO4E-Felder unmapped`}
            </span>
          }
          style={{ marginTop: "var(--s-3)" }}
        >
          <Stack style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-3)" }}>
            <ProgressBar
              value={
                coverage.totals.edi_anchors === 0
                  ? 0
                  : Math.round(
                      (coverage.totals.mapped_edi / coverage.totals.edi_anchors) * 100,
                    )
              }
              label={`${coverage.totals.mapped_edi}/${coverage.totals.edi_anchors} EDI-Anker zugeordnet`}
            />
            {/* Aktionierbarer Hinweis: klick filtert die Tabelle auf Review-Bedarf */}
            {coverage.totals.needs_review > 0 && (
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setRowFilter((f) => (f === "needs_review" ? "all" : "needs_review"))
                }
              >
                <Notice tone="critical">
                  {coverage.totals.needs_review} Entries brauchen BA-Review (Anker-Zuordnung oder
                  Transform offen).{" "}
                  {rowFilter === "needs_review"
                    ? "Filter aktiv — klicken zum Aufheben."
                    : "Klicken, um die Tabelle darauf zu filtern."}
                </Notice>
              </div>
            )}
          </Stack>
        </Card>
      )}

      {busy ? (
        <Flex style={{ margin: "var(--s-4)", justifyContent: "center", gap: "var(--s-2)" }}>
          <Spinner size="m" />
          <span>Lade Mappings ...</span>
        </Flex>
      ) : tableMode ? (
        <Card style={{ marginTop: "var(--s-3)" }}>
          <div style={{ padding: "var(--s-2)" }}>
            <Flex
              style={{ padding: "0 var(--s-2) var(--s-2)", gap: "var(--s-3)", alignItems: "center" }}
            >
              <input
                className="prn-input"
                placeholder="Suche: Anker, nr, BO4E-Pfad, Transform, Status …"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "420px" }}
                data-testid="global-search"
              />
              {searchText && (
                <span style={{ fontSize: "0.8rem", color: "var(--sapContent_LabelColor)" }}>
                  {tableData.length} Treffer
                </span>
              )}
            </Flex>
            <AnalyticalTable
              data={tableData}
              columns={columns}
              visibleRows={15}
              minRows={5}
              sortable
              filterable
              onRowClick={({ row }) => {
                const entry = (row as { entry?: MappingEntry } | undefined)?.entry;
                if (entry) setWizard({ mode: "edit", entry, msgType });
              }}
              header={`${tableData.length} Mapping-Entries${rowFilter === "needs_review" ? " (gefiltert: Review-Bedarf)" : ""}`}
            />
          </div>
        </Card>
      ) : (
        <MappingBridge
          key={`${perspective}-${perspective === "bo" ? boSelection : msgType}`}
          rows={rows}
          leftSide={sides.left}
          onAddMapping={(r) => r.ediAnchor && setWizard({ mode: "create", anchor: r.ediAnchor })}
          onOpenEntry={(mappingId) => {
            void openEntryById(mappingId);
          }}
          onAddBoSource={(row) =>
            setWizard({ mode: "create", bo4eTarget: { bo: row.group, path: row.spine.name } })
          }
        />
      )}

      <TestPanel
        open={testOpen}
        onClose={() => setTestOpen(false)}
        bundleId={bundle?.id ?? ""}
        messageType={msgType}
      />

      <ReviewPanel
        open={reviewOpen}
        result={reviewResult}
        loading={reviewLoading}
        error={reviewError}
        onClose={() => setReviewOpen(false)}
        onAccept={(f) => void acceptFinding(f)}
        onAcceptAll={() => void acceptAllFindings()}
        onOpenEntry={(f) => {
          setReviewOpen(false);
          const bo = f.bo4e_anchor?.bo;
          const path = f.bo4e_anchor?.path;
          const match = entries.find(
            (e) => e.bo4e_anchor.bo === bo && e.bo4e_anchor.path === path,
          );
          if (match) setWizard({ mode: "edit", entry: match, msgType });
          else if (f.edi_anchor)
            setWizard({ mode: "create", anchor: f.edi_anchor as unknown as EdiAnchor });
        }}
      />

      {wizard && bundle && (
        <EntryEditWizard
          bundleId={bundle.id}
          msgType={wizard.mode === "edit" ? wizard.msgType : msgType}
          mode={wizard.mode}
          entry={wizard.mode === "edit" ? wizard.entry : null}
          presetAnchor={wizard.mode === "create" && "anchor" in wizard ? wizard.anchor : undefined}
          presetBo4e={
            wizard.mode === "create" && "bo4eTarget" in wizard ? wizard.bo4eTarget : undefined
          }
          ediAnchors={ediAnchors}
          bo4eTargets={bo4eTargets}
          onClose={() => setWizard(null)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}
